/**
 * RedFlagsCard — liste de points de vigilance générés par Claude.
 *
 * UX spec :
 *   - Un item par red flag, trié par sévérité (critical → warn → info).
 *   - Chip couleur à gauche (rose / amber / lime).
 *   - Titre en gras, detail en texte courant, source en chip discrète.
 *   - État vide ≠ erreur : "Aucun point de vigilance détecté" avec un ton
 *     positif (check vert). C'est un résultat légitime et rassurant.
 *   - Erreur : soft-fail, pas de bannière rouge.
 *   - Skeleton pendant loading : 3 lignes grises.
 */

import { useAiRedFlags, type AiRedFlag, type AiRedFlagSeverity } from "../../lib/ai/redFlags";
import { Card } from "../ui";
import { Ic } from "../icons";

type Props = { ficheId: string };

export function RedFlagsCard({ ficheId }: Props) {
  const state = useAiRedFlags(ficheId);

  return (
    <Card className="p-5 md:p-6">
      <header className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-ink-50 hairline grid place-items-center text-ink-700">
          <Ic.Shield size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.1em] text-lime-700 font-medium">
            Analyse IA
          </div>
          <h3 className="text-[16px] font-semibold tracking-tight text-ink-900">
            Points de vigilance
            {state.status === "ready" && state.data.flags.length > 0 && (
              <span className="ml-2 text-[12px] font-normal text-ink-500">
                ({state.data.flags.length})
              </span>
            )}
          </h3>
        </div>
      </header>

      <div className="mt-4">
        {state.status === "idle" || state.status === "loading" ? (
          <Skeleton />
        ) : state.status === "error" ? (
          <ErrorBox />
        ) : state.data.flags.length === 0 ? (
          <EmptyState />
        ) : (
          <ul className="space-y-3">
            {state.data.flags.map((f, i) => (
              <FlagRow key={i} flag={f} />
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}

// ─── Row ─────────────────────────────────────────────────────────────────

const SEVERITY_PALETTE: Record<
  AiRedFlagSeverity,
  { bar: string; chip: string; label: string; icon: React.ReactNode }
> = {
  critical: {
    bar: "bg-rose-500",
    chip: "bg-rose-50 text-rose-800 border-rose-200",
    label: "Critique",
    icon: <Ic.Shield size={12} />,
  },
  warn: {
    bar: "bg-amber-400",
    chip: "bg-amber-50 text-amber-800 border-amber-200",
    label: "Attention",
    icon: <Ic.Shield size={12} />,
  },
  info: {
    bar: "bg-lime-400",
    chip: "bg-lime-50 text-ink-800 border-lime-200",
    label: "Info",
    icon: <Ic.Sparkle size={12} />,
  },
};

// Reverse-map of sourceRef slugs to human labels. Kept in the UI layer —
// Claude emits slugs, we render them. New slugs appear as-is (escape hatch).
const SOURCE_LABEL: Record<string, string> = {
  "fiche-reglement": "Fiche · Règlement PLU",
  "fiche-existant": "Fiche · Bien existant",
  "fiche-dvf": "Fiche · Comparables DVF",
  "fiche-pricetrend": "Fiche · Évolution des prix",
  "env-dpe": "Environnement · DPE voisins",
  "env-ecoles": "Environnement · Écoles",
  "env-commerces": "Environnement · Commerces",
  "env-transports": "Environnement · Transports",
  "env-arcep": "Environnement · Couverture fibre",
  "env-abf": "Environnement · Protections patrimoniales",
  "env-bruit": "Environnement · Bruit aéronautique",
  "env-filosofi": "Environnement · Profil du quartier",
  "env-loyers": "Environnement · Carte des loyers",
};

function FlagRow({ flag }: { flag: AiRedFlag }) {
  const p = SEVERITY_PALETTE[flag.severity];
  return (
    <li className="flex gap-3">
      <div className={`w-1 rounded-full shrink-0 ${p.bar}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`inline-flex items-center gap-1 text-[10.5px] font-medium uppercase tracking-wide rounded-md border px-1.5 py-0.5 ${p.chip}`}
          >
            {p.icon}
            {p.label}
          </span>
          <span className="text-[14px] font-semibold text-ink-900 leading-snug">
            {flag.title}
          </span>
        </div>
        <p className="mt-1 text-[13px] text-ink-700 leading-relaxed">
          {flag.detail}
        </p>
        {flag.sourceRef && (
          <div className="mt-1 text-[11.5px] text-ink-500">
            → {SOURCE_LABEL[flag.sourceRef] ?? flag.sourceRef}
          </div>
        )}
      </div>
    </li>
  );
}

// ─── Empty state ─────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div className="flex items-start gap-3 rounded-lg bg-emerald-50 border border-emerald-100 px-4 py-3">
      <div className="w-7 h-7 rounded-full bg-emerald-500 grid place-items-center text-paper shrink-0">
        <Ic.Check size={14} />
      </div>
      <div>
        <div className="text-[13.5px] font-medium text-emerald-900">
          Aucun point de vigilance détecté.
        </div>
        <div className="text-[12px] text-emerald-800 mt-0.5 leading-snug">
          La fiche et son environnement ne présentent pas de contradiction ni
          de contrainte structurante au-delà du régime PLU standard.
        </div>
      </div>
    </div>
  );
}

// ─── Skeleton ────────────────────────────────────────────────────────────

function Skeleton() {
  return (
    <ul className="space-y-3 animate-pulse" aria-hidden>
      {[0, 1, 2].map((i) => (
        <li key={i} className="flex gap-3">
          <div className="w-1 rounded-full bg-ink-100" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 rounded bg-ink-100 w-2/3" />
            <div className="h-2.5 rounded bg-ink-100 w-full" />
            <div className="h-2.5 rounded bg-ink-100 w-4/5" />
          </div>
        </li>
      ))}
    </ul>
  );
}

// ─── Error fallback ──────────────────────────────────────────────────────

function ErrorBox() {
  return (
    <div className="rounded-lg bg-ink-50 px-4 py-3 text-[13px] text-ink-700 leading-relaxed">
      Les points de vigilance IA sont indisponibles pour le moment. Réessayez
      dans quelques minutes.
    </div>
  );
}
